import React, { useState, useEffect, useRef } from "react";
import {
  Upload, Package, Check, X, AlertTriangle, RefreshCw, FileText,
  Play, Clock, CheckCircle, XCircle, SkipForward, Loader,
  Trash2, StopCircle,
} from "lucide-react";
import api from "../api/client";

const statusIcon = (s) => {
  if (s === "created") return <CheckCircle size={14} className="text-green-400" />;
  if (s === "skipped") return <SkipForward size={14} className="text-yellow-400" />;
  if (s === "failed") return <XCircle size={14} className="text-red-400" />;
  if (s === "pending") return <Clock size={14} className="text-gray-500" />;
  return <Loader size={14} className="text-blue-400 animate-spin" />;
};

const statusColor = (s) => {
  if (s === "created") return "text-green-400";
  if (s === "skipped") return "text-yellow-400";
  if (s === "failed") return "text-red-400";
  return "text-gray-500";
};

const statusBadge = (s) => {
  const map = {
    completed: "bg-green-900/60 text-green-300",
    failed: "bg-red-900/60 text-red-300",
    importing: "bg-blue-900/60 text-blue-300",
    cancelled: "bg-orange-900/60 text-orange-300",
    previewing: "bg-gray-800/60 text-gray-300",
  };
  return map[s] || "bg-gray-800/60 text-gray-300";
};

export default function BulkImport({ selectedSite }) {
  const [phase, setPhase] = useState("upload"); // upload | preview | importing | done
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [preview, setPreview] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [publishStatus, setPublishStatus] = useState("draft");
  const [importStatus, setImportStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const siteId = selectedSite?.id;

  // Load history
  const loadHistory = () => {
    if (!siteId) return;
    api.get(`/bulk-import/history/?site_id=${siteId}`)
      .then((r) => setHistory(r.data.imports || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadHistory();
  }, [siteId, phase]);

  // Poll during import
  useEffect(() => {
    if (phase !== "importing" || !jobId) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/bulk-import/status/${jobId}/`);
        setImportStatus(r.data);
        if (["completed", "failed", "cancelled"].includes(r.data.status)) {
          clearInterval(pollRef.current);
          setPhase("done");
          setCancelling(false);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [phase, jobId]);

  // Upload handler
  const handleFile = async (file) => {
    if (!file || !siteId) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("site_id", siteId);
    try {
      const r = await api.post("/bulk-import/upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobId(r.data.job_id);
      const items = r.data.preview || [];
      setPreview(items);
      setSelected(new Set(items.map((p) => p.slug)));
      setPhase("preview");
    } catch (e) {
      alert("Upload failed: " + (e.response?.data?.error || e.message));
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const toggleAll = () => {
    if (selected.size === preview.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(preview.map((p) => p.slug)));
    }
  };

  const toggleSlug = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleExecute = async () => {
    if (!jobId || selected.size === 0) return;
    try {
      await api.post("/bulk-import/execute/", {
        job_id: jobId,
        publish_status: publishStatus,
        selected_slugs: Array.from(selected),
      });
      setPhase("importing");
      const r = await api.get(`/bulk-import/status/${jobId}/`);
      setImportStatus(r.data);
    } catch (e) {
      alert("Execute failed: " + (e.response?.data?.error || e.message));
    }
  };

  const handleCancel = async () => {
    if (!jobId) return;
    setCancelling(true);
    try {
      await api.post(`/bulk-import/${jobId}/cancel/`);
    } catch (e) {
      alert("Cancel failed: " + (e.response?.data?.error || e.message));
      setCancelling(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this import record?")) return;
    try {
      await api.delete(`/bulk-import/${id}/delete/`);
      setHistory((prev) => prev.filter((h) => h.id !== id));
    } catch (e) {
      alert("Delete failed: " + (e.response?.data?.error || e.message));
    }
  };

  const handleCancelHistory = async (id) => {
    try {
      await api.post(`/bulk-import/${id}/cancel/`);
      loadHistory();
    } catch (e) {
      alert("Cancel failed: " + (e.response?.data?.error || e.message));
    }
  };

  const resetForm = () => {
    setPhase("upload");
    setJobId(null);
    setPreview([]);
    setSelected(new Set());
    setImportStatus(null);
    setCancelling(false);
  };

  if (!siteId) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a website to use Bulk Import.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Package size={24} className="text-copper" />
            Bulk Import
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Import HTML pages from a ZIP file to WordPress
          </p>
        </div>
        {phase !== "upload" && (
          <button
            onClick={resetForm}
            className="px-4 py-2 bg-dark-200 border border-dark-50 text-gray-400 hover:text-white rounded-lg text-sm"
          >
            New Import
          </button>
        )}
      </div>

      {/* Phase: Upload */}
      {phase === "upload" && (
        <div className="space-y-4">
          <div
            className={`border-2 border-dashed rounded-2xl p-12 text-center transition ${
              dragActive
                ? "border-copper bg-copper/5"
                : "border-dark-50 hover:border-gray-600"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => handleFile(e.target.files[0])}
            />
            {uploading ? (
              <RefreshCw size={48} className="text-copper mx-auto mb-4 animate-spin" />
            ) : (
              <Upload size={48} className="text-gray-600 mx-auto mb-4" />
            )}
            <h3 className="text-lg font-bold text-white mb-2">
              {uploading ? "Processing ZIP..." : "Drop ZIP file here"}
            </h3>
            <p className="text-sm text-gray-500">
              or click to browse. Supports nested folders and flat ZIP structures up to 200 MB.
            </p>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="bg-dark-200 border border-dark-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Import History</h3>
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-sm py-2 border-b border-dark-50 last:border-0">
                    <div className="flex items-center gap-3">
                      <FileText size={14} className="text-gray-500" />
                      <span className="text-gray-300">{h.filename}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${statusBadge(h.status)}`}>
                        {h.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="text-green-400">{h.created} created</span>
                      {h.skipped > 0 && <span className="text-yellow-400">{h.skipped} skipped</span>}
                      {h.failed > 0 && <span className="text-red-400">{h.failed} failed</span>}
                      <span>{new Date(h.created_at).toLocaleDateString()}</span>
                      {/* Cancel button for active imports */}
                      {(h.status === "importing" || h.status === "previewing") && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCancelHistory(h.id); }}
                          className="p-1 text-orange-400 hover:text-orange-300 hover:bg-orange-900/30 rounded transition"
                          title="Cancel import"
                        >
                          <StopCircle size={14} />
                        </button>
                      )}
                      {/* Delete button for completed/failed/cancelled imports */}
                      {["completed", "failed", "cancelled", "previewing"].includes(h.status) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(h.id); }}
                          className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition"
                          title="Delete record"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phase: Preview */}
      {phase === "preview" && (
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex items-center justify-between bg-dark-200 border border-dark-50 rounded-xl p-4">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleAll}
                className="text-xs text-copper hover:text-copper-400"
              >
                {selected.size === preview.length ? "Deselect All" : "Select All"}
              </button>
              <span className="text-sm text-gray-400">
                {selected.size} of {preview.length} selected
              </span>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={publishStatus}
                onChange={(e) => setPublishStatus(e.target.value)}
                className="bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-1.5"
              >
                <option value="draft">Import as Draft</option>
                <option value="publish">Import as Published</option>
              </select>
              <button
                onClick={handleExecute}
                disabled={selected.size === 0}
                className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium transition disabled:opacity-30"
              >
                <Play size={16} />
                Import {selected.size} Pages
              </button>
            </div>
          </div>

          {/* Preview table */}
          <div className="bg-dark-200 border border-dark-50 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs border-b border-dark-50">
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={selected.size === preview.length}
                      onChange={toggleAll}
                      className="rounded bg-dark-300 border-dark-50"
                    />
                  </th>
                  <th className="text-left px-3 py-3">Page Title</th>
                  <th className="text-left px-3 py-3">Slug</th>
                  <th className="text-left px-3 py-3">Service</th>
                  <th className="text-left px-3 py-3">City</th>
                  <th className="text-left px-3 py-3">Parent</th>
                  <th className="text-left px-3 py-3">SEO Title</th>
                  <th className="text-left px-3 py-3 max-w-[200px]">Meta Desc</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((p) => (
                  <tr
                    key={p.slug}
                    className={`border-t border-dark-50 transition ${
                      selected.has(p.slug) ? "bg-dark-300/30" : "opacity-50"
                    }`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(p.slug)}
                        onChange={() => toggleSlug(p.slug)}
                        className="rounded bg-dark-300 border-dark-50"
                      />
                    </td>
                    <td className="px-3 py-2 text-white font-medium">{p.title}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs font-mono">{p.slug}</td>
                    <td className="px-3 py-2">
                      {p.service ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-copper/20 text-copper border border-copper/30">
                          {p.service}
                        </span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-400">{p.city || "—"}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{p.parent_slug || "—"}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs truncate max-w-[180px]">
                      {p.seo_title || <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-[200px]">
                      {p.meta_desc || <span className="text-gray-600">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Phase: Importing / Done */}
      {(phase === "importing" || phase === "done") && importStatus && (
        <div className="space-y-4">
          {/* Progress summary */}
          <div className="bg-dark-200 border border-dark-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white">
                {importStatus.status === "cancelled"
                  ? "Import Cancelled"
                  : phase === "importing"
                  ? "Importing..."
                  : "Import Complete"}
              </h3>
              <div className="flex items-center gap-2">
                {phase === "importing" && !cancelling && (
                  <button
                    onClick={handleCancel}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-900/30 border border-orange-800/50 text-orange-400 hover:text-orange-300 rounded-lg text-xs font-medium transition"
                  >
                    <StopCircle size={14} />
                    Cancel
                  </button>
                )}
                {cancelling && (
                  <span className="text-xs text-orange-400 flex items-center gap-1.5">
                    <Loader size={12} className="animate-spin" />
                    Cancelling...
                  </span>
                )}
                {phase === "importing" && !cancelling && (
                  <RefreshCw size={16} className="text-copper animate-spin" />
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>
                  {(importStatus.created_count || 0) + (importStatus.skipped_count || 0) + (importStatus.failed_count || 0)}
                  {" / "}{importStatus.total_pages || 0}
                </span>
                <span>
                  {importStatus.total_pages > 0
                    ? Math.round(
                        (((importStatus.created_count || 0) + (importStatus.skipped_count || 0) + (importStatus.failed_count || 0)) / importStatus.total_pages) * 100
                      )
                    : 0}%
                </span>
              </div>
              <div className="w-full h-3 bg-dark-300 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    importStatus.status === "cancelled" ? "bg-orange-500" : "bg-copper"
                  }`}
                  style={{
                    width: `${importStatus.total_pages > 0
                      ? (((importStatus.created_count || 0) + (importStatus.skipped_count || 0) + (importStatus.failed_count || 0)) / importStatus.total_pages) * 100
                      : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-dark-300 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-400">{importStatus.created_count || 0}</div>
                <div className="text-xs text-gray-500">Created</div>
              </div>
              <div className="bg-dark-300 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-400">{importStatus.skipped_count || 0}</div>
                <div className="text-xs text-gray-500">Skipped</div>
              </div>
              <div className="bg-dark-300 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-400">{importStatus.failed_count || 0}</div>
                <div className="text-xs text-gray-500">Failed</div>
              </div>
            </div>
          </div>

          {/* Per-page results */}
          {importStatus.results && importStatus.results.length > 0 && (
            <div className="bg-dark-200 border border-dark-50 rounded-xl overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-dark-200">
                    <tr className="text-gray-500 text-xs border-b border-dark-50">
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Page</th>
                      <th className="text-left px-4 py-2">Slug</th>
                      <th className="text-left px-4 py-2">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importStatus.results.map((r, i) => (
                      <tr key={i} className="border-t border-dark-50">
                        <td className="px-4 py-2">
                          <span className={`flex items-center gap-1.5 ${statusColor(r.status)}`}>
                            {statusIcon(r.status)}
                            <span className="text-xs capitalize">{r.status}</span>
                          </span>
                        </td>
                        <td className="px-4 py-2 text-white text-xs">{r.title}</td>
                        <td className="px-4 py-2 text-gray-500 text-xs font-mono">{r.slug}</td>
                        <td className="px-4 py-2 text-xs">
                          {r.url ? (
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-copper hover:underline">
                              View Page
                            </a>
                          ) : r.error ? (
                            <span className="text-red-400">{r.error}</span>
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
